import type { TFunction } from 'i18next'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { painel as api, type Metricas, type Painel24h, type Situacao } from '../api/painel'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Cartao } from '../componentes/Cartao'
import { Estado } from '../componentes/Estado'
import { useSessao } from '../auth/useSessao'
import { formatar } from '../i18n'
import { PainelCanais } from './PainelCanais'
import './usuarios.css'
import './painel.css'

const PERIODOS = [7, 30, 90]

/**
 * Duas perguntas, duas seções.
 *
 * O **sinal de vida** responde "está de pé agora?" — é a tela do plantão. As
 * **métricas** respondem "está valendo a pena?" — é a tela da reunião de
 * alinhamento. Juntá-las numa média só produziria um painel que não serve a
 * nenhum dos dois.
 */
export function Painel() {
  const { t } = useTranslation()
  const { pode } = useSessao()

  const [vinteQuatro, setVinteQuatro] = useState<Painel24h | null>(null)
  const [metricas, setMetricas] = useState<Metricas | null>(null)
  const [dias, setDias] = useState(30)
  const [falhou, setFalhou] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [recado, setRecado] = useState<string | null>(null)

  const carregar = useCallback(async () => {
    setFalhou(false)
    try {
      const [a, b] = await Promise.all([api.vinteQuatroHoras(), api.metricas(dias)])
      setVinteQuatro(a)
      setMetricas(b)
    } catch {
      setFalhou(true)
    }
  }, [dias])

  useEffect(() => {
    void carregar()
  }, [carregar])

  if (falhou)
    return <Estado tipo="erro" acao={<Botao onClick={carregar}>{t('comum.tentarDeNovo')}</Botao>} />

  if (!vinteQuatro || !metricas) return <Estado tipo="carregando" />

  async function enviarResumo() {
    setRecado(null)
    setEnviando(true)

    try {
      await api.enviarResumo()
      setRecado(t('painel.resumoEnviado'))
    } catch {
      setRecado(t('painel.resumoFalhou'))
    } finally {
      setEnviando(false)
    }
  }

  return (
    <main className="usuarios">
      <header className="usuarios__cabecalho">
        <div>
          <h1 className="usuarios__titulo">{t('painel.titulo')}</h1>
          <p className="usuarios__descricao">
            {t('painel.periodo', {
              de: formatar.dataHora(vinteQuatro.de),
              ate: formatar.dataHora(vinteQuatro.ate),
            })}
          </p>
        </div>

        {pode('ParametroCtgf') && (
          <Botao variante="discreto" onClick={enviarResumo} carregando={enviando}>
            {t('painel.enviarResumo')}
          </Botao>
        )}
      </header>

      {recado && <Aviso tom="info">{recado}</Aviso>}

      <Cartao titulo={t('painel.sinalDeVida')}>
        {/* A situação geral é a PIOR das partes, não a média. Um indicador
            vermelho no meio de nove verdes é um problema, e média o esconderia. */}
        <p className={`painel__geral painel__geral--${classe(vinteQuatro.situacaoGeral)}`}>
          <span aria-hidden="true">{simbolo(vinteQuatro.situacaoGeral)}</span>{' '}
          {t(`painel.situacao${vinteQuatro.situacaoGeral}`)}
        </p>

        {vinteQuatro.sinalDeVida.length === 0 ? (
          <Estado tipo="vazio" descricao={t('painel.semIndicadores')} />
        ) : (
          <ul className="painel__indicadores">
            {vinteQuatro.sinalDeVida.map((i) => (
              <li key={i.nome} className={`painel__indicador painel__indicador--${classe(i.situacao)}`}>
                <p className="painel__indicador-nome">
                  {/* UI-09: símbolo e rótulo vêm do back junto com a situação —
                      a cor nunca carrega a informação sozinha. */}
                  <span aria-hidden="true">{i.simbolo}</span> {i.nome}
                  <span className="painel__indicador-rotulo">{i.rotulo}</span>
                </p>
                <p className="painel__indicador-texto">{i.texto}</p>
                {i.detalhe && <p className="painel__indicador-detalhe">{i.detalhe}</p>}
              </li>
            ))}
          </ul>
        )}
      </Cartao>

      {/* Ficha 311: a situação POR CANAL. O sinal de vida acima diz se o atendimento
          está de pé; este bloco diz qual canal, especificamente, não está. */}
      <PainelCanais />

      <Cartao titulo={t('painel.metricas')}>
        <div className="painel__periodos" role="group" aria-label={t('painel.escolherPeriodo')}>
          {PERIODOS.map((p) => (
            <Botao
              key={p}
              variante={p === dias ? 'primario' : 'discreto'}
              onClick={() => setDias(p)}
              aria-pressed={p === dias}
            >
              {t('painel.dias', { dias: p })}
            </Botao>
          ))}
        </div>

        <dl className="painel__numeros">
          <Numero rotulo={t('painel.conversas')} valor={formatar.numero(metricas.conversas)} />
          <Numero rotulo={t('painel.mensagens')} valor={formatar.numero(metricas.mensagens)} />

          {/* 🔴 O número que sustenta o nome do projeto. Sem ele, "atendimento
              que nunca dorme" é slogan; com ele, é argumento para a Firjan. */}
          <Numero
            rotulo={t('painel.foraDoHorario')}
            valor={`${formatar.numero(metricas.percentualForaDoHorario)}%`}
            detalhe={t('painel.foraDoHorarioDetalhe', {
              total: formatar.numero(metricas.conversasForaDoHorario),
            })}
            destaque
          />

          <Numero
            rotulo={t('painel.transbordo')}
            valor={`${formatar.numero(metricas.percentualDeTransbordo)}%`}
            detalhe={t('painel.transbordoDetalhe', {
              total: formatar.numero(metricas.transbordos),
            })}
          />

          <Numero
            rotulo={t('painel.primeiraResposta')}
            valor={
              metricas.medianaDeSegundosAtePrimeiraResposta === null
                ? '—'
                : duracao(metricas.medianaDeSegundosAtePrimeiraResposta, t)
            }
            detalhe={t('painel.primeiraRespostaDetalhe')}
          />

          <Numero
            rotulo={t('painel.custoPorConversa')}
            valor={formatar.moeda(metricas.custoPorConversa)}
            detalhe={t('painel.custoTotal', { total: formatar.moeda(metricas.custo) })}
          />

          {/* Demanda 056: o copiloto tem de aparecer em algum número, senão
              daqui a um mês ninguém sabe dizer se ele ajuda ou é enfeite caro.
              Vem do resumo de 24h, e não das métricas do período — é o único
              lugar onde o número existe hoje. */}
          <Numero
            rotulo={t('painel.notasDoCopiloto')}
            valor={formatar.numero(vinteQuatro.notasDoCopiloto)}
            detalhe={t('painel.notasDoCopilotoDetalhe')}
          />
        </dl>
      </Cartao>

      <div className="painel__colunas">
        <Cartao titulo={t('painel.porCanal')}>
          {metricas.porCanal.length === 0 ? (
            <Estado tipo="vazio" />
          ) : (
            <div
                className="usuarios__tabela-rolagem"
                // A região rolável precisa ser alcançável pelo teclado (WCAG 2.1.1):
                // sem isto, a parte escondida da tabela não existe para quem não usa mouse.
                tabIndex={0}
                role="group"
                aria-label={t('painel.porCanalLegenda')}
              >
                <table className="usuarios__tabela">
                <caption className="usuarios__legenda">{t('painel.porCanalLegenda')}</caption>
                <thead>
                  <tr>
                    <th scope="col">{t('painel.canal')}</th>
                    <th scope="col">{t('marca.titulo')}</th>
                    <th scope="col">{t('painel.conversas')}</th>
                    <th scope="col">{t('painel.mensagens')}</th>
                  </tr>
                </thead>
                <tbody>
                  {metricas.porCanal.map((c) => (
                    <tr key={`${c.marca}-${c.canal}`}>
                      <td>{c.canal}</td>
                      <td>{c.marca}</td>
                      <td>{formatar.numero(c.conversas)}</td>
                      <td>{formatar.numero(c.mensagens)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Cartao>

        <Cartao titulo={t('painel.motivosDeTransbordo')}>
          {metricas.motivosDeTransbordo.length === 0 ? (
            <Estado tipo="vazio" descricao={t('painel.semTransbordo')} />
          ) : (
            <ul className="painel__barras">
              {metricas.motivosDeTransbordo.map((m) => (
                <li key={m.motivo} className="painel__barra">
                  <span className="painel__barra-rotulo">{t(`transbordo.${m.motivo}`, m.motivo)}</span>
                  <span
                    className="painel__barra-trilho"
                    // A barra é decorativa: o número ao lado já diz tudo, e
                    // largura não é informação para quem usa leitor de tela.
                    aria-hidden="true"
                  >
                    <span
                      className="painel__barra-preenchida"
                      style={{ width: `${largura(m.total, metricas.motivosDeTransbordo)}%` }}
                    />
                  </span>
                  <span className="painel__barra-valor">{formatar.numero(m.total)}</span>
                </li>
              ))}
            </ul>
          )}
        </Cartao>
      </div>

      <Cartao titulo={t('painel.porDia')}>
        {metricas.porDia.length === 0 ? (
          <Estado tipo="vazio" />
        ) : (
          <div
              className="usuarios__tabela-rolagem"
              // A região rolável precisa ser alcançável pelo teclado (WCAG 2.1.1):
              // sem isto, a parte escondida da tabela não existe para quem não usa mouse.
              tabIndex={0}
              role="group"
              aria-label={t('painel.porDiaLegenda')}
            >
              <table className="usuarios__tabela">
              <caption className="usuarios__legenda">{t('painel.porDiaLegenda')}</caption>
              <thead>
                <tr>
                  <th scope="col">{t('painel.dia')}</th>
                  <th scope="col">{t('painel.conversas')}</th>
                  <th scope="col">{t('painel.mensagens')}</th>
                </tr>
              </thead>
              <tbody>
                {metricas.porDia.map((p) => (
                  <tr key={p.dia}>
                    <td>{formatar.data(p.dia)}</td>
                    <td>{formatar.numero(p.conversas)}</td>
                    <td>{formatar.numero(p.mensagens)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Cartao>
    </main>
  )
}

function Numero({
  rotulo,
  valor,
  detalhe,
  destaque,
}: {
  rotulo: string
  valor: string
  detalhe?: string
  destaque?: boolean
}) {
  return (
    <div className={`painel__numero ${destaque ? 'painel__numero--destaque' : ''}`}>
      <dt>{rotulo}</dt>
      <dd>
        <strong>{valor}</strong>
        {detalhe && <small>{detalhe}</small>}
      </dd>
    </div>
  )
}

function classe(situacao: Situacao) {
  return situacao.toLowerCase()
}

function simbolo(situacao: Situacao) {
  return { Verde: '✓', Atencao: '!', Vermelho: '✕', Desconhecida: '?' }[situacao]
}

function largura(valor: number, todos: { total: number }[]) {
  const maior = Math.max(...todos.map((t) => t.total), 1)
  return Math.round((valor / maior) * 100)
}

/** Segundos em algo legível. Mediana de 4.500s não diz nada; "1h 15min" diz. */
function duracao(segundos: number, t: TFunction) {
  if (segundos < 60) return t('painel.segundos', { total: Math.round(segundos) })

  const minutos = Math.round(segundos / 60)
  if (minutos < 60) return t('painel.minutos', { total: minutos })

  const horas = Math.floor(minutos / 60)
  return t('painel.horas', { horas, minutos: minutos % 60 })
}
